import { createFileRoute, Link } from "@tanstack/react-router";
import { Star, Radar, Clock, UserCheck, PlaneLanding, MessageSquareText, Wifi, Globe, Handshake, Car, ShieldCheck, FileLock2, Headset, Receipt } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import CorporateAccountForm from "@/components/CorporateAccountForm";
import TextMariaSection from "@/components/TextMariaSection";
import TextUs from "@/components/TextUs";
import heroImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1600&imagetools";
import featureImage from "@/assets/sprinter-exec-orange.jpg?format=webp&quality=72&w=1400&imagetools";
import morningImage from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=1000&imagetools";

const SITE = "https://atlantaluxurysprinters.com";
const PAGE_URL = `${SITE}/corporate-accounts`;
const TITLE = "Corporate Car Service Accounts Atlanta | Private Aviation & Executive Travel";
const DESC =
  "One contact for every executive trip: planeside at PDK, FTY, RYY, LZU & ATL, flight tracking, NDAs, monthly invoicing and a vetted chauffeur network nationwide and worldwide.";
const REVIEW_COUNT = "337";

const timeline = [
  { icon: Radar, t: "We track the tail number / flight", d: "Delays and early arrivals adjust our timing automatically" },
  { icon: Clock, t: "Chauffeur and vehicle staged before wheels-down" },
  { icon: UserCheck, t: "You receive the chauffeur's name, photo and vehicle" },
  { icon: PlaneLanding, t: "Planeside or FBO lobby meet (per FBO rules); bags loaded" },
  { icon: MessageSquareText, t: "One text to you: 'Arrived and waiting'" },
  { icon: Wifi, t: "Wi-Fi, charging and privacy once you're aboard" },
];

const reasons = [
  { icon: Globe, t: "One point of contact for every trip, worldwide" },
  { icon: Handshake, t: "Personally vetted partner operators in major US and international cities" },
  { icon: Car, t: "The same first-class vehicle standard, wherever you land" },
  { icon: Radar, t: "Flight tracking on every trip, in every city" },
  { icon: ShieldCheck, t: "Background-checked chauffeurs certified by the Georgia State Commissioner, trained to executive protocol" },
  { icon: FileLock2, t: "NDAs available: what happens in the vehicle stays in the vehicle" },
  { icon: Headset, t: "24/7 text support: a real person, not a call center" },
  { icon: Receipt, t: "Monthly invoicing and saved passenger preferences for account clients" },
];

const airports = [
  { code: "PDK", name: "DeKalb-Peachtree", fbos: "Signature Aviation · Atlantic Aviation · Aero Center Atlanta", drive: "Buckhead: 18–30 min", to: "/car-service-pdk-airport" as const },
  { code: "FTY", name: "Fulton County Executive, Charlie Brown Field", fbos: "Signature Aviation · Hill Aircraft", drive: "Downtown Atlanta: 18–30 min", to: "/car-service-fulton-county-airport-atlanta" as const },
  { code: "RYY", name: "Cobb County International, McCollum Field", fbos: "Atlanta Executive Jet Center · Aero Atlanta Flight Center", drive: "The Battery / Truist Park: 18–28 min", to: "/car-service-cobb-county-airport" as const },
  { code: "LZU", name: "Gwinnett County, Briscoe Field", fbos: "Aircraft Specialists Jet Center", drive: "Alpharetta / Johns Creek: 25–40 min", to: null },
  { code: "ATL", name: "Hartsfield-Jackson", fbos: "Signature Aviation private terminal + all commercial terminals", drive: "Buckhead: 25–45 min", to: "/atlanta-airport-car-service" as const },
];

const vehicles = [
  { v: "Suburban SUV", p: "up to 6 (captain seats) or 7 (bench seats)", l: "4–5 large suitcases", b: "1–4 executives, discreet arrivals" },
  { v: "Jet Style Sprinter", p: "up to 7", l: "Up to 14 large suitcases", b: "VIPs wanting first-class captain seats and privacy" },
  { v: "Executive Sprinter", p: "up to 13", l: "Up to 14 large suitcases", b: "Leadership teams, crews + luggage, roadshows" },
  { v: "Limo Style Sprinter", p: "up to 14", l: "Up to 14 large suitcases", b: "Client entertainment and events" },
];

const testimonials = [
  { name: "Paul West", role: "Google review", text: "Kevin does a great job! He is very conscientious on how he drives. He is friendly, polite and very professional. I highly recommend ... for all your business and personal needs!" },
  { name: "Brandon Parker", role: "Google review", text: "Maria and her team are punctual and professional. One of the best car services I've ever used when traveling. The fleet of cars are extremely clean and sanitized." },
];

const faqs = [
  { q: "Do you do planeside pickups at PDK, FTY, RYY and LZU?", a: "Yes, wherever the FBO allows it. At many private terminals, your chauffeur can pull right up to the aircraft, so your passengers step off the plane and straight into the vehicle. Each FBO sets its own rules, so if planeside access isn't allowed that day, your chauffeur waits for your passengers inside the FBO lobby, greets them by name, and handles every bag. Either way, nobody walks across a parking lot or waits at a curb." },
  { q: "What happens if the flight is early or delayed?", a: "Nothing changes for you. We track every flight in real time, so if it lands early or late, our timing adjusts on its own. Your chauffeur is in place before the wheels touch down, whether that is 30 minutes early or two hours late. You never need to call us with an update, and your passengers are never left waiting." },
  { q: "Can you handle trips outside Atlanta or outside the US?", a: "Yes. You book once with us, and we take care of the rest. For trips outside Atlanta, across the USA or around the world, we arrange a first-class vehicle and chauffeur through partner companies we have personally vetted. You get one point of contact, the same standard of service, and no need to research a new car company in every city." },
  { q: "Do you sign NDAs?", a: "Yes. We are happy to sign an NDA for any account or any single trip. Our chauffeurs are trained in discretion: they don't discuss who they drive, where they go, or what is said in the vehicle. What happens in the vehicle stays in the vehicle." },
  { q: "How does monthly invoicing work for corporate accounts?", a: "Instead of paying for each ride separately, your company receives one invoice at the end of the month covering every trip. Each trip is listed clearly with the date, the passenger and the pickup and drop-off, so your accounting team can match everything quickly. There is no setup fee and no commitment." },
  { q: "How quickly can you respond to a last-minute request?", a: "Text or call us any time, day or night, at (678) 431-7222. A real person answers, not a call center, and confirms availability as quickly as possible. The more notice you give us the better, but we always do our best to make last-minute trips happen." },
  { q: "How much luggage fits?", a: "It depends on the vehicle. The Suburban SUV fits 4 to 5 large suitcases. The Jet Style, Executive and Limo Style Sprinters each fit up to 14 large suitcases. See the vehicle comparison table above for passengers and luggage side by side.", link: true },
];

const strip = (s: string) => s.replace(/\s*\[CONFIRM[^\]]*\]/g, "").trim();

export const Route = createFileRoute("/corporate-accounts")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Corporate & Private Aviation Accounts", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Corporate & Private Aviation Accounts",
          serviceType: "Corporate and private aviation ground transportation accounts",
          url: PAGE_URL,
          areaServed: [
            { "@type": "City", name: "Atlanta, GA" },
            { "@type": "State", name: "Georgia" },
            { "@type": "Country", name: "United States" },
          ],
          provider: {
            "@type": "LocalBusiness",
            name: "Atlanta Luxury Sprinters",
            url: SITE,
            telephone: "+1-678-431-7222",
            address: {
              "@type": "PostalAddress",
              streetAddress: "3401 Norman Berry Dr Suite 146",
              addressLocality: "Atlanta",
              addressRegion: "GA",
              postalCode: "30344",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: strip(f.a) } })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Header />
      <main>
        {/* HERO */}
        <section data-track-location="hero" className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-24 pb-12">
          <div className="absolute inset-0">
            <img src={heroImage} alt="Jet Style Mercedes-Benz Sprinter with white leather captain seats for executive travel" width={1920} height={1080} className="w-full h-full object-cover" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-purple-light text-xs sm:text-sm font-medium tracking-[0.3em] uppercase mb-6">
              Corporate &amp; Private Aviation Accounts: One Contact, Every Trip, Every City
            </h1>
            <p className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-6 text-white">
              Your Executive Lands. We're Already There.
            </p>
            <p className="text-white/80 text-base sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Flight-tracked, planeside pickups across Atlanta, and the same first-class standard in every city your travel takes you. One text and it's handled.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#corporate-account" className="btn-primary-hero rounded-sm">Set Up a Corporate Account</a>
              <TextUs className="btn-outline-hero rounded-sm inline-flex items-center justify-center gap-2" />
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <div className="bg-foreground text-background">
          <p className="max-w-6xl mx-auto px-4 py-4 text-center text-xs sm:text-sm tracking-wide">
            <span className="text-gold" aria-label="5 stars">★★★★★</span> {REVIEW_COUNT} Google reviews, rated 5.0 · 16 years in Atlanta · Women-owned · NDAs available · Flight tracking on every trip
          </p>
        </div>

        {/* TIMELINE */}
        <section className="section-padding">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-12">The Wheels-Down Timeline</h2>
            <ol className="relative grid gap-8 lg:grid-cols-6 lg:gap-4">
              <div className="hidden lg:block absolute top-7 left-[8%] right-[8%] h-px bg-gold/40" aria-hidden="true" />
              {timeline.map(({ icon: Icon, t, d }, i) => (
                <li key={t} className="relative flex lg:flex-col items-start lg:items-center gap-4 lg:text-center">
                  <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full border border-gold/50 bg-background text-gold">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-gold font-medium mb-1">Step {i + 1}</p>
                    <p className="text-foreground font-medium leading-snug">{t}</p>
                    {d && <p className="text-sm text-muted-foreground mt-1">{d}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* A REAL MORNING */}
        <section className="section-padding bg-surface">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-2 overflow-hidden rounded-md border border-border bg-card">
              <img src={morningImage} alt="Executive Sprinter cabin with facing seats and conference table" loading="lazy" decoding="async" className="h-64 md:h-full w-full object-cover" />
              <div className="p-6 sm:p-10">
                <p className="text-primary text-xs tracking-[0.3em] uppercase mb-3 font-medium">A Real Morning</p>
                <ul className="space-y-3 font-heading text-lg">
                  <li><span className="text-gold">7:40am:</span> your CEO lands at PDK.</li>
                  <li><span className="text-gold">7:48am:</span> bags are loaded and the Sprinter is moving.</li>
                  <li><span className="text-gold">8:15am:</span> arrival at the Buckhead office, prepared for the 8:30 board meeting.</li>
                </ul>
                <p className="mt-5 text-muted-foreground">You got two texts: 'Arrived and waiting' and 'Dropped off.' That's it.</p>
              </div>
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="section-padding">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-10">Why Assistants and Flight Departments Choose Us</h2>
            <div className="relative overflow-hidden rounded-md mb-8">
              <img src={featureImage} alt="Executive Sprinter cabin with ambient lighting, ready for an executive arrival" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/30" />
              <div className="relative p-6 sm:p-12 max-w-2xl text-white">
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold mb-4">From Atlanta to Anywhere Your Travel Takes You</h3>
                <p className="text-white/85 leading-relaxed mb-4">
                  Your executive lands in Miami, New York or London, and the experience is exactly the same: a first-class Sprinter or luxury SUV waiting, a professional chauffeur and the standard you expect from us in Atlanta.
                </p>
                <p className="text-white/85 leading-relaxed">
                  For 16 years we've built and personally vetted a network of trusted partner operators across the USA and worldwide. You don't have to find someone reliable in every city or country. You book once with us, and we coordinate everything.
                </p>
              </div>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {reasons.map(({ icon: Icon, t }) => (
                <li key={t} className="rounded-md border border-border bg-card p-5">
                  <Icon className="h-6 w-6 text-gold mb-3" aria-hidden="true" />
                  <p className="text-sm text-foreground leading-relaxed">{t}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* AIRPORTS */}
        <section className="section-padding bg-surface">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-10">Atlanta-Area Private Airports We Serve</h2>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {airports.map((a) => (
                <li key={a.code} className="flex flex-col rounded-md border border-border bg-card p-6">
                  <p className="font-heading text-3xl text-gold">{a.code}</p>
                  <p className="font-heading text-lg text-foreground mt-1">{a.name}</p>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{a.fbos}</p>
                  <p className="mt-4 text-sm text-foreground"><Clock className="inline h-4 w-4 mr-1 text-gold" aria-hidden="true" />{a.drive}</p>
                  {a.to && (
                    <Link to={a.to} className="mt-3 text-sm text-primary underline underline-offset-2">{a.code} car service</Link>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-center text-sm text-muted-foreground mt-8">
              Flying into another FBO or another city? We coordinate it for you.{" "}
              <Link to="/private-jet-airports-georgia" className="text-primary underline underline-offset-2">See every Georgia private airport we serve</Link>
            </p>
          </div>
        </section>

        {/* VEHICLES */}
        <section id="vehicles" className="section-padding">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-10">Choose the Right Vehicle</h2>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-foreground text-background">
                  <tr>
                    <th className="p-4 font-medium">Vehicle</th>
                    <th className="p-4 font-medium">Passengers</th>
                    <th className="p-4 font-medium">Luggage</th>
                    <th className="p-4 font-medium">Best for</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.v} className="border-t border-border bg-card">
                      <td className="p-4 font-heading text-base text-foreground">{v.v}</td>
                      <td className="p-4">{v.p}</td>
                      <td className="p-4 text-muted-foreground">{v.l}</td>
                      <td className="p-4 text-muted-foreground">{v.b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* PROOF */}
        <section className="section-padding bg-surface">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <div className="flex justify-center gap-1 mb-3" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" />)}
            </div>
            <p className="font-heading text-xl sm:text-2xl mb-2">{REVIEW_COUNT} Google reviews, rated 5.0 · 16 years in Atlanta</p>
            <p className="text-muted-foreground mb-10">Trusted by executive assistants and flight departments across Atlanta</p>
            <div className="grid gap-6 md:grid-cols-2 text-left">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-md border border-border bg-card p-6">
                  <blockquote className="text-foreground leading-relaxed">"{t.text}"</blockquote>
                  <figcaption className="mt-4 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{t.name}</span>, {t.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <CorporateAccountForm />

        {/* FAQ */}
        <section className="section-padding">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((f) => (
                <div key={f.q} className="border-b border-border pb-6">
                  <h3 className="font-heading text-lg text-foreground mb-2">{f.q}</h3>
                  <p className="text-muted-foreground">
                    {f.a}
                    {f.link && <> <a href="#vehicles" className="text-primary underline underline-offset-2">View the vehicle table</a></>}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <TextMariaSection
          title="Rather Just Text Me?"
          body="Tell me who's traveling, where they land and when. I'll handle it personally, account or not, and send your quote in writing."
        />
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}
